import { useState, type RefObject } from 'react';
import { Vote } from 'lucide-react';
import { CreatePollSheet } from './CreatePollSheet';
import { Capacitor } from '@capacitor/core';
import { cn } from '@/lib/utils';
import { useScrollOffset } from '@/hooks/useScrollOffset';
import { triggerHaptic } from '@/hooks/useHaptics';
import { useEngagementGate } from '@/hooks/useEngagementGate';
import { toast } from 'sonner';

interface CreatePollFABProps {
  scrollRef?: RefObject<HTMLElement | null>;
}

export function CreatePollFAB({ scrollRef }: CreatePollFABProps) {
  const [isOpen, setIsOpen] = useState(false);
  const fallbackRef = { current: null } as RefObject<HTMLElement | null>;
  const isScrolled = useScrollOffset(scrollRef || fallbackRef, 20);
  const { hasEngagedSaves, savedCount, threshold } = useEngagementGate();

  return (
    <>
      <div 
        className={cn(
          "fixed right-4 z-50 transition-all duration-300 ease-out origin-center",
          isScrolled && "scale-0 opacity-0"
        )}
        style={{ bottom: Capacitor.isNativePlatform()
          ? 'calc(5.25rem + env(safe-area-inset-bottom))'
          : '6.25rem'
        }}
      >
        <button
          onClick={() => {
            triggerHaptic('medium');
            if (!hasEngagedSaves) {
              toast(`Save ${threshold - savedCount} more item${threshold - savedCount === 1 ? '' : 's'} to create polls`, {
                description: 'Build your collection first!',
              });
              return;
            }
            window.dispatchEvent(new Event('pause-all-feed-videos'));
            setIsOpen(true);
          }}
          className="w-14 h-14 flex items-center justify-center active:scale-95 transition-transform rounded-full shadow-lg border border-primary/20 bg-brand-gradient"
          aria-label="Create poll"
        >
          <Vote 
            className="w-7 h-7 text-primary-foreground"
            strokeWidth={2.5}
          />
        </button>
      </div>

      <CreatePollSheet open={isOpen} onOpenChange={setIsOpen} />
    </>
  );
}
